import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import handle401 from '../handle-401';
import HttpClient from '../../Api/HttpClient';
import { transformCardGroupToUI } from './playbook-queries';

export function transformAPIProjectToUI(project) {
  const tasks = [];
  if (project.serializer_name === 'BoardGroupedTasksSerializer') {
    const singleTasks = project.standalone_cards.map((card) => transformAPITaskToUI(card));
    const groups = project.grouped_cards.map((group) =>
      transformCardGroupToUI(group, transformAPITaskToUI),
    );
    tasks.push(...singleTasks, ...groups);
  } else {
    tasks.push(...project.cards.map((card) => transformAPITaskToUI(card)));
  }

  return {
    id: project.id,
    name: project.name,
    crmId: project.crm_id,
    value: project.project_value,
    flags: {
      green: project.nb_green_flags,
      amber: project.nb_amber_flags,
      red: project.nb_red_flags,
    },
    targetCloseDate: project.target_close_date,
    buyerCompany: project.buyer_company_details,
    owner: project.owner_name,
    archived: project.archived,
    closed: project.closed,
    closedReason: project.closed_reason,
    playbookId: project.source_template,
    externalTeam: project.ext_assignee_team.map((user) => transformAPIUserTOUI(user)),
    internalTeam: project.int_assignee_team.map((user) => transformAPIUserTOUI(user)),
    description: project.description,
    cardMaxDate: project.card_max_date,
    createdAt: project.created_at,
    lastActivity: project.last_activity_dt,
    thumbnailUrl: project.thumbnail_url,
    priority: project.priority,
    lastUpdateType: project.last_update_type,
    thumbnailFile: project.thumbnail_file,
    forecastStatus: project.forecast_status,
    totalCards: project.total_cards,
    totalClosedCards: project.total_closed_cards,
    boardMessageCount: project.board_message_count,
    predictedEndDate: project.board_likely_end_date,
    percentage: project.percentage_completed,
    descriptionHtml: project.description_is_html,
    customAttributes: project.custom_attributes,
    meddpiccScores: project.meddpicc_scores,
    meddpiccStatus: project.meddpicc_status,
    displayGroupMaap: project.display_group_maap,
    targetQuarterEndDate: project.target_quarter_end_date,
    rushedEndDate: project.likely_end_date_rush,
    projectType: project.project_type,
    statusLabel: project.status_label,
    targetMonthEndDate: project.target_month_end_date,
    forecastStatusMonthly: project.forecast_status_monthly,
    tasks,
  };
}

function transformAPIUserTOUI(user) {
  if (!user) {
    return null;
  }

  let initials = '';
  if (user.first_name && user.last_name) {
    initials = user.first_name[0] + user.last_name[0];
  } else if (user.first_name) {
    initials = user.first_name[0];
  } else if (user.last_name) {
    initials = user.last_name[0];
  }

  return {
    id: user.id,
    initials,
    firstName: user.first_name,
    lastName: user.last_name,
    avatar: user.avatar,
    email: user.email,
    phoneNumber: user.phone_number,
  };
}

function transformAPITaskToUI(card) {
  return {
    id: card.id,
    name: card.title,
    startDate: card.start_date,
    endDate: card.end_date,
    displayEndDate: card.display_end_date,
    actualCompletionDate: card.actual_completion_date,
    taskTypeLabel: card.task_type_details?.custom_label || 'unknown',
    taskType: {
      id: card.task_type_details?.id,
      label: card.task_type_details?.custom_label,
      color: card.task_type_details?.color,
      lastCompletionTime: card.task_type_details?.last_completion_time,
      averageCompletionTime: card.task_type_details?.last5_avg_completion_time,
    },
    duration: card.duration,
    offset: card.offset,
    documentsCount: card.document_count,
    description: card.description,
    weightPercentage: card.weight_percentage,
    weight: card.weight,
    stepsCount: card.steps_count,
    doneStepsCount: card.done_steps_count,
    playbookOwnerAssigned: card.template_owner_assigned,
    internalAssignee: transformAPIUserTOUI(card.internal_assignee_details),
    externalAssignee: transformAPIUserTOUI(card.external_assignee_details),
    timing: card.task_timing,
    clientVisible: card.client_visible,
    referencedFlowName: card.ref_task_name,
    referencedFlowId: card.ref_task_id,
    completed: card.is_completed,
    side: card.side,
  };
}

export function projectQuery({ projectId, groupView = true }) {
  return queryOptions({
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
    queryKey: ['project', projectId, groupView],
    queryFn: handle401(async () => {
      const { data } = await HttpClient.getPlaybook({ id: projectId, groupView });

      return transformAPIProjectToUI(data[0]);
    }),
  });
}

export function projectTagsQuery({ projectId }) {
  return queryOptions({
    placeholderData: keepPreviousData,
    queryKey: ['project-tags', projectId],
    queryFn: handle401(async () => {
      const { data } = await HttpClient.fetchTags({ id: projectId });

      return data;
    }),
  });
}

export function getAvailableProjectStatuses({ projectId }) {
  return queryOptions({
    placeholderData: keepPreviousData,
    queryKey: ['project-statuses', projectId],
    queryFn: handle401(async () => {
      const { data } = await HttpClient.getStatuses({ id: projectId });

      return data.map((status) => {
        return {
          id: status.id,
          label: status.custom_label,
        };
      });
    }),
  });
}
